/**
 * Coach-side CRUD for `roster_entries` — the U7-U12 ghost-player
 * cohort that doesn't have a User row in v1 (see
 * docs/spec/22a-v1-launch-posture.md + migration 00084).
 *
 * RLS gates writes to the team's coach via `teams.coach_id =
 * auth.uid()`. Callers don't need to add the coach check client-side.
 *
 * All operations route through the active Supabase session — no local
 * fallback. If the coach is offline, the call fails with a clear
 * message rather than silently writing to localStorage; a future
 * offline-first pass can add a queue here, but for v1 the data is
 * coach-owned and lives canonically in Postgres.
 */
import { supabase } from './supabase';
import { validateRosterEntry, type RosterEntryInput } from '@4cm/core/rosterEntry';

export type RosterEntryCrudResult =
  | { ok: true; id: string }
  | { ok: false; error: string };

/** Add a roster entry. Validates client-side first, then INSERTs. */
export async function addRosterEntry(input: RosterEntryInput): Promise<RosterEntryCrudResult> {
  const validation = validateRosterEntry(input);
  if (!validation.ok) {
    return { ok: false, error: validation.errors[0]?.message ?? 'Invalid input.' };
  }

  const { data, error } = await supabase
    .from('roster_entries')
    .insert({
      team_id: input.team_id,
      first_name: input.first_name.trim(),
      last_name: input.last_name.trim(),
      dob: input.dob,
      position: input.position?.trim() || null,
      jersey_number: input.jersey_number ?? null,
      parent_user_id: input.parent_user_id ?? null,
      notes: input.notes?.trim() || null,
    })
    .select('id')
    .single();
  if (error) return { ok: false, error: error.message };
  return { ok: true, id: data.id as string };
}

/**
 * Update a roster entry. Partial input — only provided fields are
 * written. Validates by merging into a synthetic full input so the
 * length / range / date checks still fire on the changed fields.
 */
export async function updateRosterEntry(
  id: string,
  patch: Partial<RosterEntryInput>,
): Promise<RosterEntryCrudResult> {
  // Validate against a synthetic full input — the existing row's
  // unchanged fields are not visible here, so we only enforce on
  // what's actually being set. team_id can't change.
  const validation = validateRosterEntry({
    // Provide non-empty stand-ins so required-field rules don't
    // fire when the field isn't being changed.
    team_id: patch.team_id ?? '00000000-0000-0000-0000-000000000000',
    first_name: patch.first_name ?? '__unchanged__',
    last_name: patch.last_name ?? '__unchanged__',
    dob: patch.dob ?? '2000-01-01',
    position: patch.position,
    jersey_number: patch.jersey_number,
    parent_user_id: patch.parent_user_id,
    notes: patch.notes,
  });
  // Only fail validation on fields actually being patched.
  if (!validation.ok) {
    const relevant = validation.errors.find((e) => e.field in patch);
    if (relevant) return { ok: false, error: relevant.message };
  }

  const update: Record<string, unknown> = {};
  if (patch.first_name != null) update.first_name = patch.first_name.trim();
  if (patch.last_name != null) update.last_name = patch.last_name.trim();
  if (patch.dob != null) update.dob = patch.dob;
  if (patch.position !== undefined) update.position = patch.position?.trim() || null;
  if (patch.jersey_number !== undefined) update.jersey_number = patch.jersey_number ?? null;
  if (patch.parent_user_id !== undefined) update.parent_user_id = patch.parent_user_id ?? null;
  if (patch.notes !== undefined) update.notes = patch.notes?.trim() || null;

  if (Object.keys(update).length === 0) {
    return { ok: true, id };
  }

  const { error } = await supabase
    .from('roster_entries')
    .update(update)
    .eq('id', id);
  if (error) return { ok: false, error: error.message };
  return { ok: true, id };
}

/** Soft-delete a roster entry (sets deleted_at). */
export async function deleteRosterEntry(id: string): Promise<RosterEntryCrudResult> {
  const { error } = await supabase
    .from('roster_entries')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) return { ok: false, error: error.message };
  return { ok: true, id };
}
